import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WHATS_NEW_ENTRIES } from '../data/whatsNew';

/**
 * /whats-new — the public, shareable changelog page (2026-09-15, Nina's
 * "sheet + route" ruling). Renders the SAME data file as the header pill,
 * all entries, grouped by date. Exists for link-outs (posts, Discord) and
 * as a fresh-content signal for Search Console.
 */

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
function longDate(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  if (!m) return dateStr;
  return `${MONTHS_LONG[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

export default function WhatsNewPage() {
  const navigate = useNavigate();
  useEffect(() => {
    const prev = document.title;
    document.title = "What's new — myspaice";
    return () => { document.title = prev; };
  }, []);

  // group consecutive same-date entries under one date heading
  const groups = [];
  WHATS_NEW_ENTRIES.forEach((entry) => {
    const last = groups[groups.length - 1];
    if (last && last.date === entry.date) last.entries.push(entry);
    else groups.push({ date: entry.date, entries: [entry] });
  });

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <button type="button" onClick={() => navigate('/explore')} style={styles.brand}>
          <h1 style={styles.title}>myspa<span style={styles.titleAi}>i</span>ce</h1>
        </button>
        <p style={styles.pageHead}>What's new</p>
        <p style={styles.pageSub}>Everything that recently changed around here.</p>

        {groups.map((group) => (
          <section key={group.date} style={styles.group}>
            <h2 style={styles.groupDate}>{longDate(group.date)}</h2>
            {group.entries.map((entry) => (
              <div key={entry.date + entry.title} style={styles.entry}>
                <div style={styles.entryEmoji} aria-hidden="true">{entry.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={styles.entryTitle}>{entry.title}</h3>
                  <p style={styles.entryText}>{entry.body}</p>
                  {entry.command && (
                    <p style={styles.chipRow}>
                      <code style={styles.chip}>{entry.command}</code>
                      <span style={styles.chipHint}>{entry.commandHint || 'type it in chat'}</span>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </section>
        ))}

        <div style={styles.footer}>
          <button type="button" onClick={() => navigate('/explore')} style={styles.footerLink}>
            Back to the spaces
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', background: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  container: { maxWidth: 640, margin: '0 auto', padding: '40px 20px 60px', boxSizing: 'border-box' },
  brand: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' },
  title: { fontSize: '2rem', fontWeight: 300, color: '#111', margin: 0 },
  titleAi: { color: '#43a047', fontWeight: 600 },
  pageHead: {
    fontSize: '1.2rem', color: '#333', fontWeight: 600, letterSpacing: '1.5px',
    fontFamily: "Georgia, 'Times New Roman', serif", margin: '10px 0 2px',
  },
  pageSub: { fontSize: '0.85rem', color: '#777', margin: '0 0 30px' },
  group: { marginBottom: 26 },
  groupDate: {
    fontSize: '0.72rem', fontWeight: 700, color: '#999', letterSpacing: '0.08em',
    textTransform: 'uppercase', margin: '0 0 10px', paddingBottom: 6,
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  entry: { display: 'flex', gap: 14, padding: '10px 0' },
  entryEmoji: {
    width: 38, height: 38, borderRadius: 11, background: '#f3eef8', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.15rem',
  },
  entryTitle: { fontSize: '0.95rem', fontWeight: 700, color: '#111', margin: 0 },
  scopeTag: {
    fontSize: '0.62rem', fontWeight: 600, color: '#8a7f96', letterSpacing: '0.06em',
    marginLeft: 8, textTransform: 'uppercase',
  },
  entryText: { fontSize: '0.85rem', color: '#555', lineHeight: 1.5, margin: '3px 0 0' },
  chipRow: { display: 'inline-flex', alignItems: 'center', gap: 7, margin: '8px 0 0' },
  chip: {
    fontFamily: "'SF Mono', Menlo, monospace", fontSize: '0.78rem', color: '#7b1fa2',
    background: '#f3eef8', border: '1px solid rgba(123,31,162,0.25)', borderRadius: 6,
    padding: '3px 9px',
  },
  chipHint: { fontSize: '0.72rem', color: '#999' },
  footer: { marginTop: 30, textAlign: 'center' },
  footerLink: {
    background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
    fontSize: '0.8rem', fontWeight: 600, color: '#7b1fa2',
  },
};
